import { AlertResponse } from '@eagle/core-data-types';
import { FeatureTypes } from '@eagle/data-function-types';
import { FormatTimestamp, getAlertTypeStyle, getEventLabelWithFlag, getFeatureLabelWithFlag, Icon, Nullable, useBoolFlag, useCustomRoutes } from '@eagle/react-common';
import { useNotableEvents } from '@eagle/react-common/src/hooks/use-notable-events';
import { Button, IconButton, Popover, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';
import { tableStyles } from './video-segment.styles';

interface Props {
  alert: AlertResponse;
}

const ViewAlertButton = styled(Button)(({ theme }) => ({ margin: theme.spacing(3) }));
const StyledIconButton = styled(IconButton)({
  marginLeft: 0,
  marginRight: 0,
  marginTop: '5px',
  marginBottom: '5px',
  padding: 0,
  zIndex: 1,
});

const StyledPopover = styled(Popover)({
  zIndex: 1600,
});

export const AlertSegmentCell: FC<Props> = ({ alert }) => {
  const { alert: customRoutesAlert } = useCustomRoutes();
  const notableEvents = useNotableEvents();
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const alertHref = useHref(`/${customRoutesAlert}/${alert.alertId}`);
  const { t } = useTranslation(['track']);
  const { classes } = tableStyles();
  const theme = useTheme();
  const alertStyle = getAlertTypeStyle(alert, theme, notableEvents);
  const { alertTypeId, feature, featureTypeId } = alert;
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');

  const featureDisplay = getFeatureLabelWithFlag(feature, flagUseEventV3);
  const alertDisplay = getEventLabelWithFlag(featureTypeId, alertTypeId, feature, flagUseEventV3);
  const title = feature === FeatureTypes.EVENT_RECORD_V0 ? featureDisplay : alertDisplay;
  const subtitle = feature === FeatureTypes.EVENT_RECORD_V0 ? alertDisplay : featureDisplay;

  return <>
    <StyledPopover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={() => setPopoverOpen(false)}
      open={popoverOpen}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Stack direction="row">
        <Stack margin="20px">
          <Typography>{title}</Typography>
          <Typography variant="body2">{subtitle}</Typography>
          <FormatTimestamp value={alert.occurred} />
        </Stack>
        <ViewAlertButton href={alertHref} variant="text">{t('track:page.camera-content.action.view-alert')}</ViewAlertButton>
      </Stack>
    </StyledPopover>
    <StyledIconButton
      onClick={(event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setPopoverOpen(true);
      }}
      size="small"
    >
      <Icon icon="Bell" style={{ color: alertStyle.color, zIndex: 5, fontSize: '0.875rem' }} />
      <Box className={classes.alertCellBackground} sx={{ background: alertStyle.background }} />
    </StyledIconButton>
  </>;
};
